import { cx } from "class-variance-authority";
import { ErrorBoundary } from "react-error-boundary";

import { type DocumentMetadata } from "@moment/api-collab/api-types";

import { EaselStateContext } from "../easel";
import { useCreateEasel } from "../easel/useCreateEasel";
import { usePageEditorState } from "../easel/usePageEditorState";
import { BigMarkdownEditor } from "../editor/BigMarkdownEditor";
import { EditorHeader } from "../editor/EditorHeader";
import { Banners } from "../editor/banners/Banners";
import { NavigationPane } from "../navigation-pane";
import { NavigationPaneContextProvider } from "../navigation-pane/NavigationPaneContext";
import { CanvasTopBar } from "../top-bar/CanvasTopBar";
import { CanvasTopBarContent } from "../top-bar/CanvasTopBarContent";
import { CanvasFailure } from "./CanvasFailure";
import { CANVAS_BODY_ID } from "./constants";

const TOP_BUFFER_ID = "top-buffer";

interface Props {
	workspaceName: string;
	documentMetadata: DocumentMetadata;
	activePageId: string;
}

export function MarkdownPage({ workspaceName, documentMetadata, activePageId }: Props) {
	const { markdownEditorState: editorState } = usePageEditorState(
		documentMetadata.id,
		activePageId
	);

	const easelState = useCreateEasel({
		workspaceName,
		documentId: documentMetadata.id,
		pageId: activePageId,
		documentInteractionMode: "interactive",
	});

	return (
		<ErrorBoundary FallbackComponent={CanvasFailure}>
			<EaselStateContext.Provider value={easelState}>
				<NavigationPaneContextProvider canDelete={false}>
					{/* ------ TOP BAR ------ */}
					<CanvasTopBar>
						<CanvasTopBarContent />
					</CanvasTopBar>

					<main className="bg-primary @container/main relative flex flex-1 flex-col focus:outline-none">
						{/* --- BANNERS --- */}
						<Banners canvas={documentMetadata} />

						{/* --- CANVAS --- */}
						<div className="relative flex flex-auto">
							<div
								className={cx(
									"absolute",
									"print:static",
									"flex",
									"print:block",
									"inset-0",
									"overflow-x-hidden",
									"print:overflow-visible",
									"print:bg-primary"
								)}
								style={{
									transform: "translateZ(0)",
								}}
							>
								{/* --- PAGES PANE --- */}
								<NavigationPane />
								<div className="flex grow flex-col overflow-y-auto">
									{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
									<div
										id={CANVAS_BODY_ID}
										className={cx(
											"relative",
											"print:static",
											"flex flex-auto flex-col",
											"print:block",
											"print:overflow-visible",
											"scroll-smooth"
										)}
									>
										{/* Nav Pane Toggle Goes Here */}

										<EditorHeader writable />

										<div id={TOP_BUFFER_ID} style={{ minHeight: "32px" }} />

										<BigMarkdownEditor editorState={editorState} />
									</div>
								</div>
							</div>
						</div>
					</main>
				</NavigationPaneContextProvider>
			</EaselStateContext.Provider>
		</ErrorBoundary>
	);
}
